import { useEffect, useState, type CSSProperties } from 'react'
import { CheckCircle2, ExternalLink, KeyRound, Loader2, PlugZap, XCircle, HelpCircle } from 'lucide-react'

import Modal from '@/components/common/Modal'
import {
  connectIOFlowApp,
  getConnectFields,
  getOpenInIOFlowUrl,
  listConnectableApps,
  listNeededApps,
  testIOFlowConnection,
  type ConnectField,
  type ConnectableApp,
  type ConnectionTestResult,
  type NeededApp,
} from '@/api/integration'

interface Props {
  project: string
  open: boolean
  /** The app the agent named, when it knew which one it needed. */
  appId?: string
  onClose: () => void
  /** Called with the sentence to send back to the agent — an id and IOFlow's
   *  test verdict, never a secret. */
  onConnected: (agentReply: string, subscriptionId: string) => void
  /** Opened by the user from the console ("+ → Connect an application"), not
   *  by an agent question: the title says so, and testing an already-connected
   *  application is a complete use of the form on its own. */
  standalone?: boolean
}

const fieldStyle: CSSProperties = {
  width: '100%',
  marginTop: '0.25rem',
  padding: '0.4rem 0.5rem',
  background: 'var(--color-bg-secondary)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  color: 'var(--color-text)',
}

function errText(e: any): string {
  return e?.response?.data?.detail || e?.message || String(e)
}

/** One line of IOFlow's connection-test verdict. */
function TestVerdict({ result }: { result: ConnectionTestResult }) {
  const tone =
    result.verdict === 'passed'
      ? 'var(--color-success, #2e7d32)'
      : result.verdict === 'unknown'
        ? 'var(--color-text-secondary)'
        : 'var(--color-error, #f44336)'
  const Icon =
    result.verdict === 'passed' ? CheckCircle2 : result.verdict === 'unknown' ? HelpCircle : XCircle
  const detail = result.detail || {}
  const endpoint = typeof detail.endpoint_tested === 'string' ? detail.endpoint_tested : ''
  return (
    <div
      role="status"
      style={{
        display: 'flex',
        gap: '0.5rem',
        alignItems: 'flex-start',
        padding: '0.5rem 0.625rem',
        border: `1px solid ${tone}`,
        borderRadius: 'var(--radius-md)',
        fontSize: '0.9375rem',
      }}
    >
      <Icon size={16} style={{ color: tone, flexShrink: 0, marginTop: 2 }} />
      <div style={{ minWidth: 0 }}>
        <div>{result.message}</div>
        {endpoint && (
          <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', overflowWrap: 'anywhere' }}>
            Checked: {endpoint}
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Connect (subscribe) an IOFlow application so a flow can call it, and test
 * the connection with IOFlow's OWN connection test.
 *
 * The whole point of this component is that the credential never touches the
 * agent: the user types it here, it goes straight to the backend and on to
 * IOFlow (which encrypts it), and what comes back — and what the agent is
 * told — is a subscription id plus the test's verdict. The same shape as the
 * database ConnectionDefineFlow, for the same reason.
 *
 * Fields are asked for from the server rather than hardcoded per app: they are
 * derived from the application's own auth_type/auth_config in IOFlow, so a
 * connector added to IOFlow tomorrow — including one the seed-app designer
 * built for this project from the vendor's API — is connectable without a
 * release here.
 *
 * The applications THIS project's feeds and flows use are listed first, with
 * whether each is connected; any connected one can be tested on the spot.
 *
 * OAuth2 is deliberately NOT offered as a form. It cannot work as one — it
 * needs a browser round-trip to the provider — so this says so and hands the
 * user the deep link, then lets them test once they have authorized it.
 */
export default function IOFlowConnectForm({
  project,
  open,
  appId,
  onClose,
  onConnected,
  standalone = false,
}: Props) {
  const [apps, setApps] = useState<ConnectableApp[]>([])
  const [needed, setNeeded] = useState<NeededApp[]>([])
  const [selected, setSelected] = useState(appId || '')
  const [fields, setFields] = useState<ConnectField[]>([])
  const [authType, setAuthType] = useState('')
  const [needsBrowser, setNeedsBrowser] = useState(false)
  const [values, setValues] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [reconnect, setReconnect] = useState(false)
  const [test, setTest] = useState<ConnectionTestResult | null>(null)
  /** Set once THIS form connected the app — the reply owed to the agent. */
  const [connected, setConnected] = useState<{ reply: string; subscriptionId: string } | null>(null)
  const [error, setError] = useState('')

  const loadLists = () => {
    setLoading(true)
    // The needed list is a convenience: if it fails the full picker still works.
    Promise.all([
      listConnectableApps(project),
      listNeededApps(project).catch(() => [] as NeededApp[]),
    ])
      .then(([all, need]) => {
        setApps(all)
        setNeeded(need)
        if (!appId && !selected) {
          const first = need.find((n) => !n.connected && n.exists)
          if (first) setSelected(first.app_id)
        }
      })
      .catch((e) => setError(errText(e)))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    if (!open) return
    setError('')
    setTest(null)
    setConnected(null)
    setReconnect(false)
    if (appId) setSelected(appId)
    loadLists()
    // Reloaded on every open: a connector built or connected since the last
    // open must show its real state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, appId, project])

  useEffect(() => {
    if (!open || !selected) return
    setLoading(true)
    setError('')
    setTest(null)
    setReconnect(false)
    getConnectFields(project, selected)
      .then((r) => {
        setFields(r.fields || [])
        setAuthType(r.auth_type || '')
        setNeedsBrowser(!!r.needs_browser)
        const seed: Record<string, string> = {}
        for (const f of r.fields || []) seed[f.name] = f.default || ''
        setValues(seed)
      })
      .catch((e) => setError(errText(e)))
      .finally(() => setLoading(false))
  }, [open, selected, project])

  const openInIOFlow = async () => {
    try {
      const url = await getOpenInIOFlowUrl(project, 'application')
      window.open(url, '_blank', 'noopener')
    } catch (e) {
      setError(errText(e))
    }
  }

  const runTest = async (): Promise<ConnectionTestResult | null> => {
    if (!selected) return null
    setTesting(true)
    setError('')
    try {
      const r = await testIOFlowConnection(project, selected)
      setTest(r)
      return r
    } catch (e) {
      setError(`The connection test could not run: ${errText(e)}`)
      return null
    } finally {
      setTesting(false)
    }
  }

  const missing = fields.filter((f) => f.required && !(values[f.name] || '').trim())

  const submit = async () => {
    if (!selected || missing.length > 0) return
    setSaving(true)
    setError('')
    try {
      const res = await connectIOFlowApp(project, selected, values)
      // Clear immediately — no reason for a secret to sit in component state
      // any longer than the request it was needed for.
      setValues({})
      if (!res.ok) {
        setError('IOFlow did not return a subscription. Check the credentials.')
        return
      }
      setReconnect(false)
      // IOFlow's own test, straight away: a wrong key is found now, not on the
      // flow's first run.
      const verdict = await runTest()
      const reply =
        res.agent_reply +
        (verdict ? ` IOFlow's connection test: ${verdict.verdict} — ${verdict.message}` : '')
      setConnected({ reply, subscriptionId: res.subscription_id })
      setApps((prev) => prev.map((a) => (a.app_id === selected ? { ...a, connected: true } : a)))
      setNeeded((prev) => prev.map((a) => (a.app_id === selected ? { ...a, connected: true } : a)))
    } catch (e: any) {
      setValues({})
      setError(errText(e))
    } finally {
      setSaving(false)
    }
  }

  // An application that was ALREADY connected (or authorized in IOFlow for
  // OAuth) and passes the test is also an answer the agent can continue on.
  const testedReply =
    !connected && test && test.verdict === 'passed' && test.subscription_id
      ? {
          reply:
            `${test.app_name || 'The application'} is connected in IOFlow and passed ` +
            `IOFlow's connection test. Use subscription ` +
            `[ioflow_subscription=${test.subscription_id}] and continue.`,
          subscriptionId: test.subscription_id,
        }
      : null
  const answer = connected || (!standalone ? testedReply : null)

  const finish = () => {
    if (answer) onConnected(answer.reply, answer.subscriptionId)
    else onClose()
  }

  const app = apps.find((a) => a.app_id === selected)
  const neededIds = new Set(needed.map((n) => n.app_id))
  const isConnected = !!(app?.connected || needed.find((n) => n.app_id === selected)?.connected)
  const showFields = !!selected && !needsBrowser && (!isConnected || reconnect) && !connected

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={standalone ? 'Connect and test an application' : 'Connect an application'}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <p style={{ margin: 0, fontSize: '0.9375rem', color: 'var(--color-text-secondary)' }}>
          {standalone
            ? 'Connect an application your flows call, or test one that is already connected. '
            : 'The flow needs to call this application. '}
          Your credentials go straight to IOFlow, which stores them encrypted — they are
          never shown to the agent or saved here. The test is IOFlow&apos;s own connection test.
        </p>

        {needed.length > 0 && (
          <div data-tw="ioflow-needed-apps">
            <div style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.25rem' }}>
              Used by this application
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              {needed.map((n) => (
                <button
                  key={n.app_id}
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setSelected(n.app_id)}
                  disabled={!n.exists}
                  aria-pressed={selected === n.app_id}
                  style={{
                    justifyContent: 'space-between',
                    textAlign: 'left',
                    whiteSpace: 'normal',
                    height: 'auto',
                    lineHeight: 1.4,
                    borderColor: selected === n.app_id ? 'var(--color-accent)' : undefined,
                  }}
                >
                  <span style={{ minWidth: 0 }}>
                    <strong>{n.app_name}</strong>
                    <span style={{ color: 'var(--color-text-secondary)' }}>
                      {' '}
                      — {n.exists ? n.needed_for.join('; ') : 'no longer in IOFlow'}
                    </span>
                  </span>
                  <span
                    style={{
                      flexShrink: 0,
                      fontSize: '0.875rem',
                      color: n.connected ? 'var(--color-success, #2e7d32)' : 'var(--color-text-secondary)',
                    }}
                  >
                    {n.connected ? 'Connected' : 'Not connected'}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        <label style={{ fontSize: '0.875rem', fontWeight: 600 }}>
          Application
          <select value={selected} onChange={(e) => setSelected(e.target.value)} style={fieldStyle}>
            <option value="">Select an application…</option>
            {apps
              .slice()
              .sort((a, b) => Number(neededIds.has(b.app_id)) - Number(neededIds.has(a.app_id)))
              .map((a) => (
                <option key={a.app_id} value={a.app_id}>
                  {a.app_name}
                  {a.connected ? ' (connected)' : ''}
                </option>
              ))}
          </select>
        </label>

        {loading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.9375rem' }}>
            <Loader2 size={14} className="spin" /> Loading…
          </div>
        )}

        {selected && isConnected && !connected && !reconnect && (
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.9375rem' }}>
              <strong>{app?.app_name || 'This application'}</strong> is connected.
            </span>
            <span style={{ flex: 1 }} />
            {!needsBrowser && (
              <button className="btn btn-secondary btn-sm" onClick={() => setReconnect(true)}>
                <KeyRound size={12} /> Re-enter credentials
              </button>
            )}
          </div>
        )}

        {selected && needsBrowser && !connected && (
          <div
            style={{
              padding: '0.625rem',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: '0.9375rem',
            }}
          >
            <strong>{app?.app_name || 'This application'}</strong> uses OAuth sign-in, so it
            cannot be connected with a form — you approve access in IOFlow itself, then come
            back and press Test connection.
            <div style={{ marginTop: '0.5rem' }}>
              <button className="btn btn-primary btn-sm" onClick={openInIOFlow}>
                <ExternalLink size={12} /> Authorize in IOFlow
              </button>
            </div>
          </div>
        )}

        {showFields && authType === 'none' && (
          <p style={{ margin: 0, fontSize: '0.9375rem' }}>
            This application needs no credentials — connecting it is enough.
          </p>
        )}

        {showFields &&
          fields.map((f) => (
            <label key={f.name} style={{ fontSize: '0.875rem', fontWeight: 600 }}>
              {f.label}
              {f.required ? ' *' : ''}
              <input
                type={f.secret ? 'password' : 'text'}
                autoComplete={f.secret ? 'new-password' : 'off'}
                value={values[f.name] || ''}
                onChange={(e) => setValues({ ...values, [f.name]: e.target.value })}
                style={fieldStyle}
              />
            </label>
          ))}

        {test && <TestVerdict result={test} />}

        {error && (
          <div style={{ fontSize: '0.9375rem', color: 'var(--color-error, #f44336)' }}>{error}</div>
        )}

        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          {!connected && (
            <button className="btn btn-secondary btn-sm" onClick={onClose}>
              {standalone && isConnected ? 'Close' : 'Cancel'}
            </button>
          )}
          {selected && (isConnected || needsBrowser || connected) && (
            <button
              className="btn btn-secondary btn-sm"
              disabled={testing}
              onClick={() => void runTest()}
              data-tw="ioflow-test-connection"
            >
              {testing ? <Loader2 size={12} className="spin" /> : <PlugZap size={12} />}
              Test connection
            </button>
          )}
          {showFields && (
            <button
              className="btn btn-primary btn-sm"
              disabled={!selected || saving || testing || missing.length > 0}
              onClick={submit}
              data-tw="ioflow-connect"
            >
              {saving ? <Loader2 size={12} className="spin" /> : <KeyRound size={12} />}
              Connect
            </button>
          )}
          {answer && (
            <button className="btn btn-primary btn-sm" onClick={finish} disabled={testing}>
              {standalone ? 'Done' : 'Continue'}
            </button>
          )}
        </div>
      </div>
    </Modal>
  )
}
